import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { Building2, CopyIcon, TrashIcon } from 'lucide-react';
import { canWrite } from '@/features/permissions/resource-permissions';

import { env } from '@/constants/env';
import { cn } from '@/lib/utils';
import { Button } from '@/components/new-ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/new-ui/table';
import { projectAtom } from '@/features/navigation/atoms/projectAtom';
import { getDate } from '@/utils/getDate';
import { getNumberFormatter } from '@/utils/getNumberFormatter';
import { parseSearchParamsToSearchQuery } from '@/utils/parseSearchParamsToSearchQuery';

import { useDeleteEstate } from '../estates/api/estates';
import { ProjectEstatesInfiniteScroll } from '../estates/components/project-estates-infinite-scroll';
import { EstateFilterDrawer } from '../estates/components/estate-filter-drawer';
import { ProjectObjectsToolbar } from './project-objects-toolbar';
import { getFloorLabel } from '../estates-property/utils/floorLabelHandler';
import {
  getProjectEstatesOptions,
  type ProjectEstates,
  useProjectEstates,
} from '../projects/api/project-estates';
import type { Project } from '../projects/api/projects';
import { useModal } from '@/components/modals/new/use-modal';
import { ConfirmDeleteModal } from '@/components/modals/confirm-delete-modal';
import { CreateProjectObjectModal } from '../../components/modals/create-project-object-modal';
import { CopyProjectObjectModal } from '@/components/modals/copy-project-object-modal';
import { EstateChips } from '../estates/components/estate-chips';
import {
  getNextSort,
  SortableHeader,
  type SortField,
  type SortState,
} from '../estates/components/estates-table';
import { isRentalMarketType } from '../estates-property/utils/priceValues';

const url = '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout/objects';
const route = getRouteApi(url);
const COLUMN_COUNT = 7;
const GRID_CLASS = 'grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-4';

type ObjectsTableProps = {
  project: Project;
};

export const ObjectsTable: React.FC<ObjectsTableProps> = ({ project }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = route.useNavigate();
  const { filters, view, sort } = route.useSearch();
  const currentSort = sort as SortState | undefined;
  const sortQuery = useMemo(
    () => (sort?.by && sort.order ? { by: sort.by, order: sort.order } : undefined),
    [sort]
  );
  const isGrid = view === 'grid';
  const parsedFilters = parseSearchParamsToSearchQuery(filters);
  const hasActiveFilters = Object.values(parsedFilters).some((value) => value !== undefined);
  const modal = useModal();
  const copyObjectModal = useModal();
  const [copyEstateId, setCopyEstateId] = useState<string>();
  const canWriteGeneral = canWrite(project.metadata?.permissions, 'general');

  const setProject = useSetAtom(projectAtom);

  const queryProjectEstatesOptions = useMemo(
    () => ({
      projectId: project?.id,
      query: {
        ...(Object.keys(parsedFilters).length ? { filters: parsedFilters } : {}),
        ...(sortQuery ? { sort: sortQuery } : {}),
      },
    }),
    [project, parsedFilters, sortQuery]
  );

  const { data } = useProjectEstates(queryProjectEstatesOptions);

  const { mutate: deleteEstate } = useDeleteEstate({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('deleteEstateSuccess'));
        queryClient.invalidateQueries({
          queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
        });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const estates = data.estates.data;

  const invalidateProjectEstates = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
    });
  }, [queryClient, queryProjectEstatesOptions]);

  const handleSort = useCallback(
    (field: SortField) => {
      navigate({
        search: (prev) => {
          const nextSort = getNextSort(prev.sort as SortState | undefined, field);

          if (!nextSort) {
            const { sort: _, ...rest } = prev;
            return rest;
          }

          return { ...prev, sort: nextSort };
        },
      });
    },
    [navigate]
  );

  const selectProject = useCallback(() => {
    if (project?.id && project?.name) {
      setProject({ id: project.id, name: project.name });
    } else {
      setProject(null);
    }
  }, [project?.id, project?.name, setProject]);

  const navigateToEstatePage = useCallback(
    (id: string) => {
      selectProject();
      navigate({ to: '/estates/$estateId', params: { estateId: id } });
    },
    [navigate, selectProject]
  );

  const handleCopyClick = (estateId: string) => {
    if (!canWriteGeneral) {
      return;
    }

    setCopyEstateId(estateId);
    copyObjectModal.open();
  };

  const onAddObjectButtonClick = useCallback(() => {
    if (!canWriteGeneral) {
      return;
    }

    modal.open();
  }, [canWriteGeneral, modal]);

  const handleDeleteEstate = (estateId: string) => {
    if (!canWriteGeneral) {
      return;
    }

    deleteEstate(estateId);
  };

  const renderEstate = (estate: ProjectEstates[number]) =>
    isGrid ? (
      <EstateCard
        estate={estate}
        selectProject={selectProject}
        handleCopyClick={handleCopyClick}
        handleDeleteEstate={handleDeleteEstate}
        canWriteGeneral={canWriteGeneral}
      />
    ) : (
      <EstateTableRow
        estate={estate}
        navigateToEstatePage={navigateToEstatePage}
        selectProject={selectProject}
        handleCopyClick={handleCopyClick}
        handleDeleteEstate={handleDeleteEstate}
        canWriteGeneral={canWriteGeneral}
      />
    );

  let emptyMessage = t('noEstatesInProjectYet');
  if (hasActiveFilters) {
    emptyMessage = t('no-estates');
  } else if (canWriteGeneral) {
    emptyMessage = t('noEstatesInProjectCreate');
  }

  return (
    <>
      <div className="flex size-full flex-col gap-6">
        <ProjectObjectsToolbar onAddEstate={canWriteGeneral ? onAddObjectButtonClick : undefined} />
        {/* The drawer is not redesigned yet, it stays outside of the new-ui scope */}
        <EstateFilterDrawer url={url} />
        <div className="new-ui">
          {isGrid ? (
            estates.length ? (
              <div className={GRID_CLASS}>
                {estates.map((estate) => (
                  <Fragment key={`project-object-${estate.id}`}>{renderEstate(estate)}</Fragment>
                ))}
                <ProjectEstatesInfiniteScroll
                  projectId={project?.id}
                  filters={filters}
                  sort={sortQuery}
                  navigateToEstatePage={navigateToEstatePage}
                  variant="grid"
                  rowRenderer={renderEstate}
                />
              </div>
            ) : (
              <div className="py-12 text-center text-muted-foreground">{emptyMessage}</div>
            )
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table className="min-w-[760px]">
                <TableHeader>
                  <TableRow>
                    <SortableHeader
                      field="name"
                      className="min-w-[260px]"
                      currentSort={currentSort}
                      onSort={handleSort}
                    >
                      {t('title')}
                    </SortableHeader>
                    <SortableHeader field="price" currentSort={currentSort} onSort={handleSort}>
                      {t('price')}
                    </SortableHeader>
                    <SortableHeader field="area" currentSort={currentSort} onSort={handleSort}>
                      {t('area')}
                    </SortableHeader>
                    <SortableHeader field="rooms" currentSort={currentSort} onSort={handleSort}>
                      {t('rooms')}
                    </SortableHeader>
                    <SortableHeader field="creator" currentSort={currentSort} onSort={handleSort}>
                      {t('author')}
                    </SortableHeader>
                    <SortableHeader field="createdAt" currentSort={currentSort} onSort={handleSort}>
                      {t('created')}
                    </SortableHeader>
                    <TableHead className="w-[80px]">
                      <span className="sr-only">{t('actions')}</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {estates.length ? (
                    <>
                      {estates.map((estate) => (
                        <Fragment key={`project-object-${estate.id}`}>
                          {renderEstate(estate)}
                        </Fragment>
                      ))}
                      <ProjectEstatesInfiniteScroll
                        projectId={project?.id}
                        filters={filters}
                        sort={sortQuery}
                        navigateToEstatePage={navigateToEstatePage}
                        variant="table-row"
                        colSpan={COLUMN_COUNT}
                        rowRenderer={renderEstate}
                      />
                    </>
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={COLUMN_COUNT}
                        className="py-8 text-center text-muted-foreground"
                      >
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
      {canWriteGeneral ? (
        <CreateProjectObjectModal
          modal={modal}
          navigate={navigate}
          project={{
            id: project?.id || '',
            name: project?.name || '',
          }}
          onSuccess={() => {
            invalidateProjectEstates();
          }}
        />
      ) : null}
      {copyEstateId ? (
        <CopyProjectObjectModal
          modal={copyObjectModal}
          estateId={copyEstateId}
          onSubmit={() => {
            queryClient.invalidateQueries({
              queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
            });
          }}
          onSuccess={() => {
            invalidateProjectEstates();
          }}
          navigate={navigate}
        />
      ) : null}
    </>
  );
};

const CHIPS_CLASS = 'flex flex-wrap items-center gap-2';
const CELL_CLASS = 'whitespace-nowrap tabular-nums text-foreground';
const EMPTY_VALUE = '-';

type EstateRowProps = {
  estate: ProjectEstates[number];
  navigateToEstatePage: (estateId: string) => void;
  selectProject: () => void;
  handleCopyClick: (estateId: string) => void;
  handleDeleteEstate: (estateId: string) => void;
  canWriteGeneral: boolean;
};

/** Thumbnail in the design look: muted tile with a building icon as the placeholder. */
const EstateThumbnail: React.FC<{ thumbnailId?: string | null; className: string }> = ({
  thumbnailId,
  className,
}) => {
  const [failed, setFailed] = useState(false);
  const src = thumbnailId ? `${env.API_URL}/images/${thumbnailId}?size=thumbnail200` : null;
  const showImage = Boolean(src) && !failed;

  return (
    <div
      className={cn('flex items-center justify-center overflow-hidden bg-muted', className)}
      aria-hidden
    >
      {showImage ? (
        <img
          src={src ?? undefined}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <Building2 className="h-5 w-5 text-muted-foreground" />
      )}
    </div>
  );
};

/** External id and floor, shown where the design shows the description. */
const getEstateSubline = (estate: ProjectEstates[number]) => {
  const floor = estate.estateAreasRooms?.floor;
  return [estate.estateDetails?.externalId, typeof floor === 'number' ? getFloorLabel(floor) : null]
    .filter(Boolean)
    .join(' \u00b7 ');
};

/** The price that matches the marketing type: rent for rent and lease, else buying. */
const formatPrice = (estate: ProjectEstates[number]) => {
  // mainRent first, like the overview tiles: it is the rent the prices form edits
  const price = isRentalMarketType(estate.estateDetails?.marketType)
    ? (estate.prices?.mainRent ?? estate.prices?.rentalPrice)
    : estate.prices?.buyingPrice;
  return price
    ? getNumberFormatter(price, 'de-DE', {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      })
    : EMPTY_VALUE;
};

const formatArea = (estate: ProjectEstates[number], mUnit: string) =>
  estate.estateAreasRooms?.livingArea
    ? `${getNumberFormatter(estate.estateAreasRooms.livingArea, 'de-DE')}${mUnit}`
    : EMPTY_VALUE;

const formatRooms = (estate: ProjectEstates[number]) =>
  estate.estateAreasRooms?.totalNumberOfRooms
    ? getNumberFormatter(estate.estateAreasRooms.totalNumberOfRooms, 'de-DE', {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      })
    : EMPTY_VALUE;

const EstateTableRow: React.FC<EstateRowProps> = ({
  estate,
  navigateToEstatePage,
  selectProject,
  handleCopyClick,
  handleDeleteEstate,
  canWriteGeneral,
}) => {
  const subline = getEstateSubline(estate);
  const creatorName = [estate.creator?.firstName, estate.creator?.lastName]
    .filter(Boolean)
    .join(' ');

  return (
    <TableRow
      data-estate-id={estate.id}
      className="cursor-pointer hover:bg-muted/50"
      onClick={(ev) => {
        // Ignore clicks that bubble from portaled dialogs and from the row actions
        if (!ev.currentTarget.contains(ev.target as Node)) return;
        if ((ev.target as HTMLElement).closest('button, a')) return;
        navigateToEstatePage(estate.id);
      }}
    >
      <TableCell className="min-w-[260px]">
        <div className="flex items-center gap-3">
          <EstateThumbnail
            // Keyed so a new thumbnail gets a fresh failed state after a failed one
            key={estate.thumbnail?.mediaId ?? 'none'}
            thumbnailId={estate.thumbnail?.mediaId}
            className="h-12 w-12 shrink-0 rounded-md"
          />
          <div className="flex min-w-0 flex-col gap-1">
            <EstateChips estate={estate} className={CHIPS_CLASS} availabilityStyle="neutral" />
            <div className="break-words font-medium text-foreground">
              <Link
                to="/estates/$estateId"
                params={{ estateId: estate.id }}
                onClick={selectProject}
              >
                {estate.name}
              </Link>
            </div>
            {subline ? (
              <div
                className="line-clamp-2 break-words text-sm text-muted-foreground"
                title={subline}
              >
                {subline}
              </div>
            ) : null}
          </div>
        </div>
      </TableCell>
      <TableCell className={CELL_CLASS}>{formatPrice(estate)}</TableCell>
      <TableCell className={CELL_CLASS}>{formatArea(estate, ' m²')}</TableCell>
      <TableCell className={CELL_CLASS}>{formatRooms(estate)}</TableCell>
      <TableCell className="text-sm text-foreground">{creatorName || EMPTY_VALUE}</TableCell>
      <TableCell className={CELL_CLASS}>{getDate(estate.createdAt)}</TableCell>
      <TableCell>
        {canWriteGeneral ? (
          <EstateActions
            estate={estate}
            handleCopyClick={handleCopyClick}
            handleDeleteEstate={handleDeleteEstate}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
};

const EstateActions: React.FC<{
  estate: ProjectEstates[number];
  handleCopyClick: (estateId: string) => void;
  handleDeleteEstate: (estateId: string) => void;
  className?: string;
}> = ({ estate, handleCopyClick, handleDeleteEstate, className }) => {
  const { t } = useTranslation();
  const deleteModal = useModal();

  return (
    <>
      <div className={className ?? 'flex items-center gap-1'}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          title={t('copy')}
          aria-label={t('copy')}
          onClick={(ev) => {
            ev.stopPropagation();
            handleCopyClick(estate.id);
          }}
        >
          <CopyIcon className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          title={t('delete')}
          aria-label={t('delete')}
          onClick={(ev) => {
            ev.stopPropagation();
            deleteModal.open();
          }}
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </div>
      <ConfirmDeleteModal
        modal={deleteModal}
        onSubmit={() => handleDeleteEstate(estate.id)}
        title={t('deleteEstate')}
        description={t('confirmDeleteEstate')}
      />
    </>
  );
};

/** Large card image, falls back to a building icon when there is none or it fails to load. */
const EstateCardImage: React.FC<{ thumbnailId?: string | null }> = ({ thumbnailId }) => {
  const [failed, setFailed] = useState(false);

  return thumbnailId && !failed ? (
    <img
      src={`${env.API_URL}/images/${thumbnailId}?size=thumbnail500`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-full w-full object-cover"
    />
  ) : (
    <Building2 className="h-10 w-10 text-muted-foreground" />
  );
};

const EstateCard: React.FC<Omit<EstateRowProps, 'navigateToEstatePage'>> = ({
  estate,
  selectProject,
  handleCopyClick,
  handleDeleteEstate,
  canWriteGeneral,
}) => {
  const subline = getEstateSubline(estate);
  const city = estate.address?.city;
  const thumbnailId = estate.thumbnail?.mediaId;

  return (
    // Plain container with a stretched title link, so the action buttons are not nested in a link
    <div className="relative flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-md">
      <div className="relative flex aspect-video shrink-0 items-center justify-center bg-muted">
        {/* Keyed so a new thumbnail gets a fresh failed state after a failed one */}
        <EstateCardImage key={thumbnailId ?? 'none'} thumbnailId={thumbnailId} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
        <EstateChips estate={estate} className={CHIPS_CLASS} availabilityStyle="neutral" compact />
        <h3 className="truncate font-semibold text-foreground" title={estate.name ?? undefined}>
          <Link
            to="/estates/$estateId"
            params={{ estateId: estate.id }}
            onClick={selectProject}
            className="after:absolute after:inset-0 after:content-['']"
          >
            {estate.name}
          </Link>
        </h3>
        {subline ? (
          <p className="line-clamp-2 break-words text-sm text-muted-foreground">{subline}</p>
        ) : null}
        {city ? <p className="truncate text-xs text-muted-foreground">{city}</p> : null}
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
            {getDate(estate.createdAt)}
          </span>
          {canWriteGeneral ? (
            <EstateActions
              estate={estate}
              handleCopyClick={handleCopyClick}
              handleDeleteEstate={handleDeleteEstate}
              className="relative z-10 flex items-center gap-1"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
